import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import Logo from '../components/Logo';
import UpgradeScreen from '../components/UpgradeScreen';
import { hasFullAccess } from '../lib/platform';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ChevronLeft, TrendingUp, TrendingDown, MapPin, Euro, Loader2,
  Trash2, ExternalLink, StickyNote, Briefcase, BarChart3, Home,
  ArrowUpRight, ArrowDownRight, CheckCircle2, XCircle, Edit3, Save,
  Share2, Link2, Copy, Check
} from 'lucide-react';

const PortfolioPage = () => {
  const { user } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingNotes, setEditingNotes] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [copiedLink, setCopiedLink] = useState(null);
  const [sharingId, setSharingId] = useState(null);

  const isClub = hasFullAccess(user?.subscription_tier);

  useEffect(() => {
    if (isClub) fetchPortfolio();
    else setLoading(false);
  }, [isClub]);

  if (!isClub) {
    return (
      <UpgradeScreen
        icon={Briefcase}
        iconBgClass="from-violet-500 to-purple-600"
        iconColorClass="text-violet-500"
        buttonGradientClass="from-violet-500 to-purple-600"
        title="Portfolio d'Investissements"
        currentTier={user?.subscription_tier}
        features={[
          { icon: TrendingUp, title: "Sauvegarde d'analyses", description: "Sauvegardez vos analyses de rentabilité depuis le chat IA" },
          { icon: BarChart3, title: "Comparaison", description: "Comparez rendements, cash-flows et crédits côte à côte" },
          { icon: StickyNote, title: "Notes personnelles", description: "Ajoutez vos notes et commentaires sur chaque bien" }
        ]}
      />
    );
  }

  const fetchPortfolio = async () => {
    try {
      const resp = await axios.get(`${API}/portfolio/list`, { withCredentials: true });
      setAnalyses(resp.data.analyses || []);
      setSummary(resp.data.summary || {});
    } catch (error) {
      console.error('Portfolio fetch error:', error);
      toast.error('Erreur lors du chargement du portfolio');
    } finally {
      setLoading(false);
    }
  };

  const deleteAnalysis = async (id) => {
    try {
      await axios.delete(`${API}/portfolio/${id}`, { withCredentials: true });
      toast.success('Analyse supprimée');
      fetchPortfolio();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const saveNotes = async (id) => {
    try {
      await axios.patch(`${API}/portfolio/${id}/notes`, { notes: noteText }, { withCredentials: true });
      toast.success('Notes sauvegardées');
      setEditingNotes(null);
      fetchPortfolio();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const shareAnalysis = async (analysisId) => {
    setSharingId(analysisId);
    try {
      const resp = await axios.post(`${API}/portfolio/${analysisId}/share`, {}, { withCredentials: true });
      const shareUrl = `${window.location.origin}/shared/${resp.data.share_token}`;
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Lien copié dans le presse-papier !');
      } catch {
        // Fallback: show link in prompt
        window.prompt('Copie ce lien pour le partager :', shareUrl);
      }
      setCopiedLink(analysisId);
      fetchPortfolio();
      setTimeout(() => setCopiedLink(null), 3000);
    } catch (error) {
      console.error('Share error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du partage');
    } finally {
      setSharingId(null);
    }
  };

  const unshareAnalysis = async (analysisId) => {
    try {
      await axios.delete(`${API}/portfolio/${analysisId}/share`, { withCredentials: true });
      toast.success('Partage désactivé');
      fetchPortfolio();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const copyShareLink = async (shareToken) => {
    const shareUrl = `${window.location.origin}/shared/${shareToken}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Lien copié !');
    } catch {
      window.prompt('Copie ce lien :', shareUrl);
    }
  };

  const sorted = [...analyses].sort((a, b) => {
    if (sortBy === 'rendement') return (b.rentabilite?.rendement_brut || 0) - (a.rentabilite?.rendement_brut || 0);
    if (sortBy === 'cashflow') return (b.rentabilite?.cash_flow_mensuel || 0) - (a.rentabilite?.cash_flow_mensuel || 0);
    if (sortBy === 'prix') return (a.bien?.prix || 0) - (b.bien?.prix || 0);
    return new Date(b.saved_at) - new Date(a.saved_at);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-[#0D47A1] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Logo size="default" linkTo="/dashboard" />
              <div className="hidden md:flex items-center space-x-2 text-slate-400">
                <span>/</span>
                <span className="text-slate-900 font-medium">Portfolio</span>
              </div>
            </div>
            <Link to="/ai-agent">
              <Button variant="outline" size="sm" data-testid="back-to-agent">
                <ChevronLeft className="w-4 h-4 mr-1" /> Agent IA
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-r from-[#0D47A1] to-[#00B050] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                <Briefcase className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold" data-testid="portfolio-title">Mon Portfolio</h1>
                <p className="text-white/80">Mes analyses d'investissement sauvegardées</p>
              </div>
            </div>
            {summary && summary.total > 0 && (
              <div className="hidden md:flex gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold">{summary.total}</div>
                  <div className="text-sm text-white/70">Analyses</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">{summary.rendement_moyen}%</div>
                  <div className="text-sm text-white/70">Rend. moy.</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold flex items-center gap-1">
                    {summary.positifs}
                    <CheckCircle2 className="h-5 w-5 text-green-300" />
                  </div>
                  <div className="text-sm text-white/70">CF positif</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="portfolio-content">
        {analyses.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Ton portfolio est vide</h2>
            <p className="text-slate-600 mb-6">
              Utilise l'Agent IA pour analyser des biens, puis clique sur "Sauvegarder" pour les retrouver ici.
            </p>
            <Link to="/ai-agent">
              <Button className="bg-gradient-to-r from-[#00B050] to-[#0D47A1]" data-testid="start-search-btn">
                <Home className="w-4 h-4 mr-2" /> Chercher un bien
              </Button>
            </Link>
          </div>
        ) : (
          <>
            {/* Sort controls */}
            <div className="flex items-center gap-2 mb-6 flex-wrap">
              <span className="text-sm text-slate-500">Trier par :</span>
              {[
                { id: 'date', label: 'Date' },
                { id: 'rendement', label: 'Rendement' },
                { id: 'cashflow', label: 'Cash-flow' },
                { id: 'prix', label: 'Prix' },
              ].map(s => (
                <Button
                  key={s.id}
                  variant={sortBy === s.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSortBy(s.id)}
                  className={sortBy === s.id ? 'bg-[#0D47A1]' : ''}
                  data-testid={`sort-${s.id}`}
                >
                  {s.label}
                </Button>
              ))}
            </div>

            {/* Comparison Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {sorted.map((a) => {
                const bien = a.bien || {};
                const rent = a.rentabilite || {};
                const credit = a.credit_25ans || {};
                const isPositif = a.verdict === 'positif';
                const isEditing = editingNotes === a.analysis_id;

                return (
                  <Card key={a.analysis_id} data-testid={`portfolio-card-${a.analysis_id}`}
                    className={`relative overflow-hidden transition-all hover:shadow-lg ${isPositif ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-red-400'}`}>
                    {/* Verdict badge */}
                    <div className="absolute top-3 right-3">
                      {isPositif ? (
                        <Badge className="bg-green-100 text-green-700 text-xs flex items-center gap-1">
                          <ArrowUpRight className="h-3 w-3" /> CF+
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 text-xs flex items-center gap-1">
                          <ArrowDownRight className="h-3 w-3" /> CF-
                        </Badge>
                      )}
                    </div>

                    <CardContent className="p-4">
                      {/* Property info */}
                      <div className="mb-3 pr-12">
                        <h3 className="font-semibold text-sm text-slate-800 line-clamp-2">{bien.titre}</h3>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                          {bien.localisation && <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{bien.localisation}</span>}
                          {bien.surface > 0 && <span>{bien.surface}m²</span>}
                          {bien.dpe && <Badge variant="outline" className="text-[10px]">DPE {bien.dpe}</Badge>}
                        </div>
                        <div className="text-xl font-bold text-[#0D47A1] mt-1">{bien.prix?.toLocaleString()}€</div>
                      </div>

                      {/* Key metrics */}
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="bg-slate-50 rounded-lg p-2 text-center">
                          <div className="text-[10px] text-slate-400 uppercase">Brut</div>
                          <div className="text-sm font-bold text-[#0D47A1]">{rent.rendement_brut?.toFixed(1)}%</div>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2 text-center">
                          <div className="text-[10px] text-slate-400 uppercase">Net</div>
                          <div className="text-sm font-bold text-[#00B050]">{rent.rendement_net?.toFixed(1)}%</div>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2 text-center">
                          <div className="text-[10px] text-slate-400 uppercase">CF/mois</div>
                          <div className={`text-sm font-bold ${rent.cash_flow_mensuel >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {rent.cash_flow_mensuel >= 0 ? '+' : ''}{rent.cash_flow_mensuel?.toLocaleString()}€
                          </div>
                        </div>
                      </div>

                      {/* Details row */}
                      <div className="text-xs text-slate-500 space-y-0.5 mb-3">
                        <div className="flex justify-between">
                          <span>Loyer estimé</span>
                          <span className="font-medium text-slate-700">{a.loyer_estime?.toLocaleString()}€/mois</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Mensualité (25 ans)</span>
                          <span className="font-medium text-slate-700">{credit.mensualite?.toLocaleString()}€</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Régime fiscal</span>
                          <span className="font-medium text-slate-700">{rent.regime_label}</span>
                        </div>
                      </div>

                      {/* Notes */}
                      {isEditing ? (
                        <div className="mb-3">
                          <Input
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder="Mes notes..."
                            className="text-xs mb-1"
                            data-testid="notes-input"
                          />
                          <div className="flex gap-1">
                            <Button size="sm" className="h-7 text-xs bg-[#0D47A1]" onClick={() => saveNotes(a.analysis_id)}>
                              <Save className="h-3 w-3 mr-1" /> OK
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingNotes(null)}>
                              Annuler
                            </Button>
                          </div>
                        </div>
                      ) : a.notes ? (
                        <div className="mb-3 bg-amber-50 rounded p-2 text-xs text-amber-800 flex items-start gap-1.5 cursor-pointer"
                          onClick={() => { setEditingNotes(a.analysis_id); setNoteText(a.notes); }}>
                          <StickyNote className="h-3 w-3 flex-shrink-0 mt-0.5" />
                          <span className="line-clamp-2">{a.notes}</span>
                        </div>
                      ) : null}

                      {/* Share banner */}
                      {a.share_token && (
                        <div className="mb-2 bg-blue-50 rounded-lg p-2 flex items-center justify-between">
                          <div className="flex items-center gap-1.5 text-xs text-[#0D47A1]">
                            <Link2 className="h-3 w-3" />
                            <span className="font-medium">Lien partagé</span>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="h-6 text-[10px] text-[#0D47A1]"
                              onClick={() => copyShareLink(a.share_token)} data-testid="copy-share-link">
                              <Copy className="h-3 w-3 mr-0.5" /> Copier
                            </Button>
                            <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-500"
                              onClick={() => unshareAnalysis(a.analysis_id)}>
                              Désactiver
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500"
                            onClick={() => { setEditingNotes(a.analysis_id); setNoteText(a.notes || ''); }}
                            data-testid="add-notes-btn">
                            <Edit3 className="h-3 w-3 mr-1" /> Notes
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-[#0D47A1]"
                            onClick={() => shareAnalysis(a.analysis_id)}
                            disabled={sharingId === a.analysis_id}
                            data-testid="share-analysis-btn">
                            {copiedLink === a.analysis_id ? (
                              <><Check className="h-3 w-3 mr-1 text-green-600" /> Copié !</>
                            ) : sharingId === a.analysis_id ? (
                              <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> ...</>
                            ) : (
                              <><Share2 className="h-3 w-3 mr-1" /> Partager</>
                            )}
                          </Button>
                          {bien.url && (
                            <a href={bien.url} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500">
                                <ExternalLink className="h-3 w-3 mr-1" /> Annonce
                              </Button>
                            </a>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => deleteAnalysis(a.analysis_id)}
                          data-testid="delete-analysis-btn">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>

                      {/* Saved date */}
                      <div className="text-[10px] text-slate-400 mt-2">
                        Sauvegardé le {new Date(a.saved_at).toLocaleDateString('fr-FR')}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default PortfolioPage;
